const STORAGE_KEY = 'meister.sidebarPinned';

let memoryPinned: boolean | null = null;
let loadPromise: Promise<boolean> | null = null;
/** Bumped on user toggle so a stale in-flight GET cannot overwrite local state. */
let localEpoch = 0;

function readSessionStorage(): boolean | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw === '1') return true;
    if (raw === '0') return false;
  } catch {
    /* noop */
  }
  return null;
}

function writeSessionStorage(pinned: boolean): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, pinned ? '1' : '0');
  } catch {
    /* noop */
  }
}

export function hasSidebarPinnedCache(): boolean {
  if (memoryPinned !== null) return true;
  return readSessionStorage() !== null;
}

export function getSidebarPinnedSnapshot(): boolean {
  if (memoryPinned !== null) return memoryPinned;
  const stored = readSessionStorage();
  if (stored !== null) {
    memoryPinned = stored;
    return stored;
  }
  return false;
}

type SetSidebarPinnedOptions = {
  /** True when the user toggled the pin control (not when applying a server fetch). */
  userInitiated?: boolean;
};

export function setSidebarPinnedSnapshot(pinned: boolean, options?: SetSidebarPinnedOptions): void {
  memoryPinned = pinned;
  if (options?.userInitiated) localEpoch += 1;
  // Keep the cached load promise in sync so a later navigation does not
  // re-apply a stale value fetched before the user toggled the preference.
  loadPromise = Promise.resolve(pinned);
  writeSessionStorage(pinned);
}

export function clearSidebarPinnedSnapshot(): void {
  memoryPinned = null;
  loadPromise = null;
  localEpoch = 0;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}

export function fetchSidebarPinnedPreference(): Promise<boolean> {
  if (loadPromise) return loadPromise;
  const epochAtFetchStart = localEpoch;
  loadPromise = fetch('/api/me/preferences')
    .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
    .then(({ ok, d }) => {
      if (localEpoch === epochAtFetchStart) {
        const pinned = ok ? Boolean(d?.sidebarPinned) : getSidebarPinnedSnapshot();
        memoryPinned = pinned;
        writeSessionStorage(pinned);
      }
      return getSidebarPinnedSnapshot();
    })
    .catch(() => getSidebarPinnedSnapshot());
  return loadPromise;
}
